import { Injectable } from "@angular/core";

import { combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { DffError } from "@dffedb/util";
import { AktuelBrugerObserver, Bruger, Rolle } from "@e-forsyning/common/bruger";
import { ForbrugerTilknytning, ForbrugerTilknytningService } from "@e-forsyning/common/forbruger-tilknytning";
import { ForsyningService } from "@e-forsyning/common/forsyninger";
import { ValgtInstallationService } from "@e-forsyning/common/valgt-installation";

import { TilknytForbrugerViewModel } from "./model/tilknyt-forbruger.view-model";
import { ForbrugerTilknytningInfo } from "./model/Tilknytning.model";
import { TilknytForbrugerFormBuilder } from "./tilknyt-forbruger-form-builder";

@Injectable({ providedIn: "root" })
export class TilknytForbrugerSandbox {
    constructor(
        private readonly forsyningService: ForsyningService,
        private readonly forbrugerTilknytningService: ForbrugerTilknytningService,
        private readonly valgtInstallationService: ValgtInstallationService,
        private readonly aktuelBrugerObserver: AktuelBrugerObserver
    ) {}

    public hentViewModel(): Observable<TilknytForbrugerViewModel> {
        const forsyninger$ = this.forsyningService.hentForsyninger();
        const tilknytningerForValgtBruger$ = this.hentTilknytningerForValgtForbruger();
        const aktualInstallationforsyningId = this.valgtInstallationService.hentForsyningId();
        const form = TilknytForbrugerFormBuilder.opretForm(aktualInstallationforsyningId);

        return combineLatest([forsyninger$, tilknytningerForValgtBruger$]).pipe(
            map(([forsyninger, tilknytningerForValgtBruger]) => ({
                forsyninger,
                tilknytningerForValgtBruger,
                form
            }))
        );
    }

    public hentTilknytningerForValgtForbruger(): Observable<ForbrugerTilknytningInfo[]> {
        const tilknytninger$ = this.forbrugerTilknytningService.hentTilknytningerForValgtForbruger();
        const bruger$ = this.aktuelBrugerObserver.valueChanges;
        return combineLatest([tilknytninger$, bruger$]).pipe(
            map(([tilknytninger, bruger]) =>
                tilknytninger.map((tilknytning) => ({
                    ...tilknytning,
                    kanTilknytningSlettes: this.kanTilknytningSlettes(tilknytning, bruger)
                }))
            )
        );
    }

    public async sletTilknytning(tilknytningId: any): Promise<any> {
        try {
            return await this.forbrugerTilknytningService.sletTilknytning(tilknytningId);
        } catch (e) {
            throw new DffError(e.message, { friendlyMessage: "Fejl i slet tilknytning.", innerError: e });
        }
    }

    public async opretTilknytning(forsyningId: string, brugernavn: string, kodeord: string): Promise<void> {
        try {
            await this.forbrugerTilknytningService.opretTilknytning(forsyningId, brugernavn, kodeord);
        } catch (e) {
            throw new DffError(e.message, { friendlyMessage: this.hentVenligBesked(e), innerError: e });
        }
    }

    private kanTilknytningSlettes(tilknytning: ForbrugerTilknytning, bruger: Bruger): boolean {
        const aktuelleEjendomNr = this.valgtInstallationService.hentEjendomNr();
        const erAktuelleInstallation = !tilknytning.erEksternTilknytning && aktuelleEjendomNr === tilknytning.ejendomNr;
        const erEjendomsadministrator = Rolle.erEjendomsadministrator(bruger && bruger.rolle);
        return !erAktuelleInstallation && !erEjendomsadministrator;
    }

    private hentVenligBesked(e: any): string {
        let besked = e.message;
        if (e.message.includes("412")) {
            besked = "Forbruger er allerede tilknyttet.";
        } else if (e.message.includes("401")) {
            besked = "Brugernavn eller kodeord er ugyldig.";
        }

        return "Fejl i tilknytning. " + besked;
    }
}
